// src/components/ui/2-organisms/ImageGallery/ImageGallery.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import SectionHeader from '../../1-molecules/SectionHeader/SectionHeader';
import ImageModal from '../ImageModal/ImageModal';
import styles from './ImageGallery.module.css';
import { getOptimizedImageUrl } from '../../../../utils/imageUtils';

const DEFAULT_GALLERY_IMAGE = 'https://placehold.co/600x400/eee/ccc?text=Gallery';

const ImageGallery = ({ images, title = 'Gallery', className = '' }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const galleryRef = useRef(null);
  const sectionRef = useRef(null); // <-- 1. ADD REF FOR THE PARENT SECTION
  const animationFrameId = useRef(null);
  const location = useLocation();
  const imageRefs = useRef([]);
  const [imageStatuses, setImageStatuses] = useState([]);

  const safeImages = useMemo(() => (Array.isArray(images) ? images : []), [images]);

  const preparedImages = useMemo(() => {
    if (!safeImages.length) return [];
    return safeImages.map((source, index) => {
      let primary = DEFAULT_GALLERY_IMAGE;
      let fallback = DEFAULT_GALLERY_IMAGE;

      if (typeof source === 'string') {
        primary = source || DEFAULT_GALLERY_IMAGE;
        fallback = source || DEFAULT_GALLERY_IMAGE;
      } else if (source && typeof source === 'object') {
        primary =
          source.optimizedUrl ||
          source.imageUrl ||
          source.url ||
          source.src ||
          source.thumbnailUrl ||
          DEFAULT_GALLERY_IMAGE;
        fallback =
          source.fallbackUrl ||
          source.originalUrl ||
          source.imageUrl ||
          source.url ||
          source.src ||
          source.thumbnailUrl ||
          primary ||
          DEFAULT_GALLERY_IMAGE;
      }

      return {
        key: `gallery-${index}`,
        optimizedUrl: getOptimizedImageUrl(primary, 'SMALL'),
        fallbackUrl: fallback || DEFAULT_GALLERY_IMAGE,
      };
    });
  }, [safeImages]);

  const barWidth = Math.max(20, 100 / (preparedImages.length || 1));

  const handleScroll = useCallback(() => {
    // Check for both refs to be safe
    if (galleryRef.current && sectionRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = galleryRef.current;
      const totalScrollableWidth = scrollWidth - clientWidth;
      const progress = totalScrollableWidth > 0 ? scrollLeft / totalScrollableWidth : 0;
      const maxLeftPosition = 100 - barWidth;
      const leftPosition = progress * maxLeftPosition;

      // --- 2. THE FIX: Update the CSS variable on the SECTION, not the gallery ---
      sectionRef.current.style.setProperty('--progress-left', `${leftPosition}%`);
    }
  }, [barWidth]);

  useEffect(() => {
    const gallery = galleryRef.current;
    const onScroll = () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      animationFrameId.current = requestAnimationFrame(handleScroll);
    };
    if (gallery) {
      gallery.addEventListener('scroll', onScroll, { passive: true });
    }
    return () => {
      if (gallery) {
        gallery.removeEventListener('scroll', onScroll);
      }
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [handleScroll]);

  // (The rest of your handlers remain the same)
  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    const newUrl = `${location.pathname}?viewImage=${index}`;
    window.history.pushState({ imageIndex: index }, '', newUrl);
  };

  const handleCloseModal = () => {
    setSelectedImageIndex(null);
    window.history.pushState({}, '', location.pathname);
  };
  
  const handlePreviousImage = () => {
    if (selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      window.history.replaceState({ imageIndex: newIndex }, '', `${location.pathname}?viewImage=${newIndex}`);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < safeImages.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      window.history.replaceState({ imageIndex: newIndex }, '', `${location.pathname}?viewImage=${newIndex}`);
    }
  };

  useEffect(() => {
    imageRefs.current = imageRefs.current.slice(0, preparedImages.length);
    setImageStatuses(prev => {
      const next = preparedImages.map((_, index) => {
        const img = imageRefs.current[index];
        if (img && img.complete) {
          return img.naturalWidth > 0 ? 'loaded' : 'error';
        }
        return prev[index] || 'loading';
      });
      return next;
    });
  }, [preparedImages]);

  const updateImageStatus = useCallback((index, status) => {
    setImageStatuses(prev => {
      if (prev[index] === status) return prev;
      const next = [...prev];
      next[index] = status;
      return next;
    });
  }, []);

  const handleImageLoad = useCallback((index) => {
    updateImageStatus(index, 'loaded');
  }, [updateImageStatus]);

  const handleImageError = useCallback((index) => {
    const item = preparedImages[index];
    const fallback = item?.fallbackUrl || DEFAULT_GALLERY_IMAGE;
    const img = imageRefs.current[index];
    if (img && fallback && img.src !== fallback) {
      updateImageStatus(index, 'loading');
      img.src = fallback;
      return;
    }
    updateImageStatus(index, 'error');
  }, [preparedImages, updateImageStatus]);

  if (!preparedImages.length) {
    return null;
  }

  return (
    <>
      {/* --- 3. APPLY THE REF AND INITIAL STYLES TO THE SECTION --- */}
      <section 
        ref={sectionRef}
        className={`${styles.gallerySection} ${className}`}
        style={{ 
          '--progress-bar-width': `${barWidth}%`,
          '--progress-left': '0%' 
        }}
      >
        <SectionHeader title={title} />
        
        <div className={styles.galleryWrapper}>
          <div 
            ref={galleryRef}
            className={styles.gallery}
            // The style attribute has been moved to the parent section
          >
            {preparedImages.map((image, index) => (
              <button
                key={index}
                className={styles.imageWrapper}
                onClick={() => handleImageClick(index)}
                aria-label={`View image ${index + 1} of ${safeImages.length}`}
              >
                <img 
                  ref={(node) => {
                    if (node) {
                      imageRefs.current[index] = node;
                    } else {
                      imageRefs.current[index] = null;
                    }
                  }}
                  src={image.optimizedUrl} 
                  alt={`Gallery image ${index + 1}`}
                  className={clsx(styles.image, {
                    [styles.imageLoaded]: imageStatuses[index] === 'loaded',
                    [styles.imageError]: imageStatuses[index] === 'error',
                    [styles.imageLoading]: imageStatuses[index] === 'loading',
                  })}
                  loading="lazy"
                  onLoad={() => handleImageLoad(index)}
                  onError={() => handleImageError(index)}
                />
              </button>
            ))}
          </div>
        </div>

        {preparedImages.length > 1 && (
          <div className={styles.progressContainer}>
            <div className={styles.progressTrack}>
              <div className={styles.progressBar} />
            </div>
          </div>
        )}
      </section>

      {selectedImageIndex !== null && (
        <ImageModal
          images={safeImages}
          currentIndex={selectedImageIndex}
          onClose={handleCloseModal}
          onPrevious={handlePreviousImage}
          onNext={handleNextImage}
        />
      )}
    </>
  );
};

export default ImageGallery;
